import React from 'react';
import { useTranslation } from 'react-i18next';
import { TherapyModality } from '@/hooks/useTherapyModalities';
import TherapyCard from './TherapyCard';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AlertTriangle } from 'lucide-react';

interface StageSectionProps {
  stageNumber: number;
  therapies: TherapyModality[];
  colorTheme?: string;
  isExpanded?: boolean;
}

const StageSection: React.FC<StageSectionProps> = ({
  stageNumber,
  therapies,
  colorTheme = 'amber',
  isExpanded = false,
}) => {
  const { t, i18n } = useTranslation('therapyLibrary');

  const getStageColorClasses = (theme: string) => {
    const colorMap: { [key: string]: string } = {
      red: 'border-red-200 bg-red-50/50',
      orange: 'border-orange-200 bg-orange-50/50',
      yellow: 'border-yellow-200 bg-yellow-50/50',
      green: 'border-green-200 bg-green-50/50',
      blue: 'border-blue-200 bg-blue-50/50',
      purple: 'border-purple-200 bg-purple-50/50',
      amber: 'border-amber-200 bg-amber-50/50',
    };
    return colorMap[theme] || 'border-amber-100 bg-amber-50/30';
  };

  const getStageColorBg = (theme: string) => {
    const colorMap: { [key: string]: string } = {
      red: 'bg-red-500',
      orange: 'bg-orange-500',
      yellow: 'bg-yellow-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      amber: 'bg-amber-600',
    };
    return colorMap[theme] || 'bg-amber-600';
  };

  // Determine color based on stage
  const stageColors: { [key: number]: string } = {
    0: 'red',
    1: 'orange',
    2: 'yellow',
    3: 'green',
    4: 'blue',
  };
  const effectiveColor = stageColors[stageNumber] || colorTheme;

  return (
    <AccordionItem
      value={`stage-${stageNumber}`}
      className={`bg-white border rounded-lg shadow-sm overflow-hidden ${getStageColorClasses(effectiveColor)}`}
    >
      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-amber-50/50 transition-colors">
        <div className="flex items-center space-x-4 text-left w-full">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${getStageColorBg(effectiveColor)}`}>
            {stageNumber}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-earth-900">
              Stage {stageNumber}: {t(`stages.${stageNumber}.title`)}
            </h3>
            <p className="text-sm text-earth-600 mt-0.5">
              {t('therapiesCount', { count: therapies.length })}
            </p>
            {stageNumber === 0 && (
              <div className="flex items-center mt-1">
                <AlertTriangle className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-sm text-red-600">{t('emergencyStage')}</span>
              </div>
            )}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-6 pb-6 pt-2">
        <div className="ml-16 space-y-4">
          {/* Stage Description */}
          <p className="text-earth-700 leading-relaxed mb-4">
            {t(`stages.${stageNumber}.description`)}
          </p>

          {/* Therapies Grid */}
          {therapies.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {therapies.map((therapy) => (
                <TherapyCard key={therapy.id} therapy={therapy} />
              ))}
            </div>
          ) : (
            <p className="text-earth-500 text-center py-4">
              {t('noResults')}
            </p>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default StageSection;
